/* ==========================================================================
 * CART CUSTOM ENGINE — Order Protection + Free Shipping Bar
 * ==========================================================================
 * Two features sharing one cart-fetch and one cart-change listener:
 *   1. Order Protection: adds/removes tier variant based on subtotal
 *   2. Free Shipping Bar: progress toward the free-shipping threshold
 *
 * Both measure the PRODUCT subtotal EXCLUDING the Order Protection line.
 *
 * --------------------------------------------------------------------------
 * MAINTENANCE:
 *   - PRICES: edit in Shopify product admin ONLY. Live prices are fetched from
 *     /products/package-assurance.js on load. PA_TIERS `price` = fallback only.
 *   - TIER BOUNDARIES (`max`) + VARIANT IDs: authoritative here. Update only
 *     if you restructure tiers or recreate variants.
 *   - PRODUCT HANDLE: the JS fetch above AND the line-item suppression in
 *     cart-drawer.liquid both key on the handle "package-assurance".
 *     DO NOT change the product handle without updating both.
 *   - Free shipping threshold: set in cart-shipping-bar.liquid
 *     (data-fs-threshold). Must match the FREESHIP discount ($250).
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // `price` is FALLBACK ONLY — live prices come from Shopify (see loadLivePrices).
  // `max` (tier ceiling in dollars) and `id` (variant ID) are authoritative here.
  var PA_TIERS = [
    { max: 50,       id: 46633297903777, price: 3 },   // OP-50
    { max: 100,      id: 46633297936545, price: 3 },   // OP-100
    { max: 150,      id: 46633297969313, price: 5 },   // OP-150
    { max: 200,      id: 46633298002081, price: 7 },   // OP-200
    { max: 250,      id: 46633298034849, price: 9 },   // OP-250
    { max: 300,      id: 46633298067617, price: 11 },  // OP-300
    { max: 350,      id: 46633298100385, price: 13 },  // OP-350
    { max: 400,      id: 46633298133153, price: 15 },  // OP-400
    { max: 450,      id: 46633298165921, price: 17 },  // OP-450
    { max: 500,      id: 46633298198689, price: 19 },  // OP-500
    { max: 550,      id: 46633298231457, price: 21 },  // OP-550
    { max: 600,      id: 46633298264225, price: 23 },  // OP-600
    { max: 650,      id: 46633298296993, price: 25 },  // OP-650
    { max: 700,      id: 46633298329761, price: 27 },  // OP-700
    { max: 750,      id: 46633298362529, price: 29 },  // OP-750
    { max: 800,      id: 46633298395297, price: 31 },  // OP-800
    { max: 850,      id: 46633298428065, price: 33 },  // OP-850
    { max: 900,      id: 46633298460833, price: 35 },  // OP-900
    { max: 950,      id: 46633298493601, price: 37 },  // OP-950
    { max: Infinity, id: 46633299214497, price: 39 }   // OP-MAX
  ];

  var PA_IDS = {};
  PA_TIERS.forEach(function (t) { PA_IDS[t.id] = true; });

  // LIVE PRICES: fetched once from the product's .js endpoint so prices are
  // read from Shopify (single source of truth). Maps variant ID -> price in
  // CENTS. Falls back to the hardcoded PA_TIERS.price (in dollars) if the
  // fetch fails, so the widget never shows a blank price.
  var PA_LIVE_PRICES = null; // { variantId: priceCents }

  function loadLivePrices() {
    if (PA_LIVE_PRICES) return Promise.resolve(PA_LIVE_PRICES);
    return fetch('/products/package-assurance.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var map = {};
        (product.variants || []).forEach(function (v) {
          map[v.id] = v.price; // Shopify returns price in cents
        });
        PA_LIVE_PRICES = map;
        return map;
      })
      .catch(function () {
        // Fallback: build the map from hardcoded dollar prices.
        var map = {};
        PA_TIERS.forEach(function (t) { map[t.id] = t.price * 100; });
        PA_LIVE_PRICES = map;
        return map;
      });
  }

  // Get the display price (in DOLLARS) for a tier, preferring the live price.
  function priceForTier(tier) {
    if (PA_LIVE_PRICES && PA_LIVE_PRICES[tier.id] != null) {
      return PA_LIVE_PRICES[tier.id] / 100;
    }
    return tier.price; // fallback to hardcoded dollars
  }

  var isBusy = false;
  var pendingRecompute = false;

  // ---- shared helpers -----------------------------------------------------

  function formatMoney(dollars) {
    return '$' + dollars.toFixed(dollars % 1 === 0 ? 0 : 2);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Product subtotal in cents EXCLUDING the Order Protection line.
  function subtotalExcludingPA(cart) {
    var cents = 0;
    cart.items.forEach(function (item) {
      if (!PA_IDS[item.variant_id]) { cents += item.final_line_price; }
    });
    return cents;
  }

  function tierForSubtotal(cents) {
    var dollars = cents / 100;
    for (var i = 0; i < PA_TIERS.length; i++) {
      if (dollars <= PA_TIERS[i].max) return PA_TIERS[i];
    }
    return PA_TIERS[PA_TIERS.length - 1];
  }

  function findPALine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (PA_IDS[cart.items[i].variant_id]) {
        return { key: cart.items[i].key, id: cart.items[i].variant_id, quantity: cart.items[i].quantity };
      }
    }
    return null;
  }

  // ========================================================================
  // FEATURE 1: ORDER PROTECTION
  // ========================================================================

  function addTier(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
  }

  function removeLineByKey(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  function money(cents) {
    // USD formatter. Drops .00 for whole-dollar amounts to match store display
    // (e.g. $82, not $82.00), but keeps cents when present (e.g. $82.50).
    var d = cents / 100;
    var hasCents = (cents % 100) !== 0;
    return '$' + d.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  // Directly update the visible subtotal + checkout button from a cart object.
  // Fallback so totals reflect reality even if section re-render misses.
  // CHANGE #8: the subtotal carries a FOMO strikethrough (compare-at total).
  // Cart JSON does not expose compare-at prices, so the Liquid stamps the
  // garments-only compare total on the element as data-compare-total (kept
  // fresh by Dawn's section swaps on every quantity change). We add the OP
  // portion (total minus garment finals) to both sides so the strike stays
  // apples-to-apples with the displayed total when OP toggles client-side.
  function patchVisibleTotals(cart) {
    var total = cart.total_price; // cents, includes OP line
    var opPortion = total - subtotalExcludingPA(cart); // cents, 0 if no OP
    // Subtotal value in drawer footer
    var subtotalEls = document.querySelectorAll(
      'cart-drawer .totals__subtotal-value, cart-drawer .totals__total-value'
    );
    subtotalEls.forEach(function (el) {
      var compareGarments = parseInt(el.getAttribute('data-compare-total') || '0', 10) || 0;
      var compareTotal = compareGarments + opPortion;
      var html;
      if (compareGarments > 0 && compareTotal > total) {
        html = '<s class="totals__compare-value">' + money(compareTotal) + '</s> ' + money(total) + ' USD';
      } else {
        html = money(total) + ' USD';
      }
      if (el.innerHTML !== html) el.innerHTML = html;
    });
    // Checkout button "Checkout · $X"
    var btn = document.getElementById('CartDrawer-Checkout');
    var btnStr = 'Checkout · ' + money(total);
    if (btn && btn.textContent.trim() !== btnStr) {
      btn.innerHTML = btnStr;
    }
    // Cart count bubble — exclude the Order Protection line so the badge
    // reflects garments only (not the OP add-on).
    patchCartCount(cart);
  }

  // Compute item count EXCLUDING Order Protection and write it to the bubble.
  function patchCartCount(cart) {
    var count = 0;
    cart.items.forEach(function (item) {
      if (!PA_IDS[item.variant_id]) { count += item.quantity; }
    });
    // Dawn's bubble: .cart-count-bubble > span[aria-hidden]
    var countStr = String(count);
    var bubbleNumbers = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
    bubbleNumbers.forEach(function (el) {
      if (el.textContent !== countStr) el.textContent = countStr;
    });
    // Also handle the visually-hidden accessible count text if present.
    var a11yStr = count + ' ' + (count === 1 ? 'item' : 'items');
    var bubbleA11y = document.querySelectorAll('.cart-count-bubble .visually-hidden');
    bubbleA11y.forEach(function (el) {
      if (el.textContent !== a11yStr) el.textContent = a11yStr;
    });
    // If cart has only the OP line (count 0), hide the bubble entirely.
    var disp = count > 0 ? '' : 'none';
    var bubbles = document.querySelectorAll('.cart-count-bubble');
    bubbles.forEach(function (b) {
      if (b.style.display !== disp) b.style.display = disp;
    });
  }

  // STALE-INDEX FIX: after JS-side cart writes (OP add/remove), the drawer's
  // rendered rows keep stale data-index values because the drawer section swap
  // no-ops (the drawer is a SNIPPET in this theme, not a section — Shopify
  // returns nothing for it in /?sections=). Dawn's qty stepper and remove
  // button send that index to /cart/change, so a stale index targets the
  // WRONG line (e.g. trash removes the hidden OP line instead of the garment).
  // This remaps each visible row's index against the fresh cart.
  //
  // Matching is by DOM order: visible rows = cart items excluding OP, in cart
  // order (holds because OP inserts at the top and garments keep relative
  // order). Writes are attribute-only and value-guarded — the MutationObserver
  // is childList-only, so this cannot feed a sync loop.
  function remapLineIndices(cart) {
    var rows = document.querySelectorAll('cart-drawer .cart-item');
    if (!rows.length) return;
    // Visible items = cart items excluding OP, with their true 1-based line numbers.
    var visible = [];
    cart.items.forEach(function (item, i) {
      if (!PA_IDS[item.variant_id]) visible.push({ item: item, line: i + 1 });
    });
    rows.forEach(function (row, n) {
      var entry = visible[n];
      if (!entry) return;
      var line = String(entry.line);
      // Dawn's updateQuantity touches FOUR index-keyed elements per row.
      // ALL must be renamed or Dawn null-crashes post-write ("There was an
      // error while updating your cart") whenever OP shifts line numbers:
      // 1. The row itself (loading spinner enable/disable)
      var rowId = 'CartDrawer-Item-' + line;
      if (row.id !== rowId) row.id = rowId;
      // 2. The quantity input (line targeting + post-write value check)
      var input = row.querySelector('.quantity__input');
      if (input && input.dataset.index !== line) {
        input.dataset.index = line;
        input.id = 'Drawer-quantity-' + line;
      }
      // 3. The remove button (line targeting)
      var removeBtn = row.querySelector('cart-remove-button');
      if (removeBtn) {
        if (removeBtn.dataset.index !== line) removeBtn.dataset.index = line;
        var rmId = 'CartDrawer-Remove-' + line;
        if (removeBtn.id !== rmId) removeBtn.id = rmId;
      }
      // 4. The line-item error div (updateLiveRegions — the null-crash source)
      var errEl = row.querySelector('.cart-item__error');
      if (errEl) {
        var errId = 'CartDrawer-LineItemError-' + line;
        if (errEl.id !== errId) errEl.id = errId;
      }
    });
  }

  // EMPTY-STATE FIX: the drawer never re-renders mid-session on OUR writes,
  // so when the cart empties the empty state ("your bag could use some
  // color") must be toggled by JS. The Liquid ALWAYS renders the empty block
  // (hidden inline at render when the cart has items). This toggles the block
  // + the is-empty classes and lets STOCK DAWN CSS own the layout, so
  // mid-session empty is pixel-identical to a page-load-empty cart.
  // Keyed on the GARMENTS-ONLY count (Dawn's own toggle uses item_count,
  // which wrongly includes the hidden OP line). Attribute-only, value-guarded
  // writes against a childList-only observer — no loop risk.
  function syncEmptyState(cart) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var count = 0;
    cart.items.forEach(function (item) {
      if (!PA_IDS[item.variant_id]) { count += item.quantity; }
    });
    var empty = count === 0;

    // The empty block: inline display none when the cart has items (it has
    // height:100% in stock CSS — anything but display:none breaks layout);
    // clear the inline style when empty so stock styling takes over fully.
    var emptyBlock = drawer.querySelector('.drawer__inner-empty');
    if (emptyBlock) {
      var disp = empty ? '' : 'none';
      if (emptyBlock.style.display !== disp) emptyBlock.style.display = disp;
    }

    // Everything else is driven by the is-empty classes + stock Dawn CSS —
    // identical to how a page-load-empty cart renders.
    if (drawer.classList.contains('is-empty') !== empty) {
      drawer.classList.toggle('is-empty', empty);
    }
    var itemsEl = drawer.querySelector('cart-drawer-items');
    if (itemsEl && itemsEl.classList.contains('is-empty') !== empty) {
      itemsEl.classList.toggle('is-empty', empty);
    }
  }

  // DAWN COUNTER SYNC: older Dawn cart.js flags a change as "rejected" (and
  // shows "You can only add N of this item to your cart") when the cart's
  // item_count after a change equals its cached this.currentItemCount from
  // the PREVIOUS change. Our OP add/remove/tier-swaps modify the cart outside
  // Dawn's knowledge, so that cache goes stale and legitimate changes can
  // coincidentally match it → false error. Keeping the cache current after
  // every one of our writes prevents the false positive. Harmless on newer
  // Dawn (property simply unused). Plain JS property — no DOM mutation.
  function syncDawnItemCount(cart) {
    var els = document.querySelectorAll('cart-drawer-items, cart-items');
    els.forEach(function (el) { el.currentItemCount = cart.item_count; });
  }

  function refreshCartUI() {
    var known = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
    return fetch('/?sections=' + known.join(','), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        known.forEach(function (id) {
          if (!data[id]) return;
          var target =
            document.getElementById(id) ||
            document.getElementById('shopify-section-' + id) ||
            document.querySelector('[id^="shopify-section-' + id + '"]');
          if (target) {
            var html = new DOMParser().parseFromString(data[id], 'text/html');
            var newInner =
              html.querySelector('#' + id) ||
              html.querySelector('.shopify-section') ||
              html.body.firstElementChild;
            if (newInner) target.innerHTML = newInner.innerHTML;
          }
        });
      })
      .then(function () {
        // Fallback: directly patch visible totals from fresh cart, in case the
        // section re-render above didn't cover this theme's drawer footer.
        return getCart().then(patchVisibleTotals);
      })
      .catch(function () {});
  }

  function reconcile(intentChecked) {
    if (isBusy) { pendingRecompute = true; return Promise.resolve(); }
    isBusy = true;
    setLoading(true);

    return getCart().then(function (cart) {
      var paLine = findPALine(cart);
      var subCents = subtotalExcludingPA(cart);
      var desiredTier = tierForSubtotal(subCents);

      if (!intentChecked) {
        if (paLine) return removeLineByKey(paLine.key).then(refreshCartUI);
        return Promise.resolve();
      }
      if (subCents <= 0) {
        if (paLine) return removeLineByKey(paLine.key).then(refreshCartUI);
        return Promise.resolve();
      }
      if (paLine && paLine.id === desiredTier.id && paLine.quantity === 1) {
        return Promise.resolve();
      }
      if (paLine) {
        return removeLineByKey(paLine.key)
          .then(function () { return addTier(desiredTier.id); })
          .then(refreshCartUI);
      }
      return addTier(desiredTier.id).then(refreshCartUI);
    }).then(function () {
      isBusy = false;
      setLoading(false);
      // After any add/remove + UI refresh, re-sync widgets against fresh cart
      // state so the (possibly re-rendered) checkbox and bar reflect reality.
      return getCart().then(function (cart) {
        bindOrderProtection();
        syncOrderProtection(cart);
        syncShippingBar(cart);
        // STALE-INDEX FIX: OP add/remove shifts line numbers but the drawer
        // rows don't re-render — remap immediately so the very next qty/trash
        // click targets the correct line.
        remapLineIndices(cart);
        // Empty state may change when OP strip empties the cart entirely.
        syncEmptyState(cart);
        // Keep Dawn's cached item count honest after our OP writes.
        syncDawnItemCount(cart);
      });
    }).then(function () {
      if (pendingRecompute) {
        pendingRecompute = false;
        var cb = currentCheckbox();
        return reconcile(cb ? cb.checked : false);
      }
    }).catch(function (e) {
      console.warn('[CartCustom] reconcile failed:', e);
      isBusy = false;
      setLoading(false);
    });
  }

  function currentWidget() { return document.querySelector('[data-pa-widget]'); }
  function currentCheckbox() { return document.querySelector('[data-pa-checkbox]'); }

  function setLoading(state) {
    var w = currentWidget();
    if (w) w.setAttribute('data-pa-loading', state ? 'true' : 'false');
  }
  function setActiveVisual(active) {
    var w = currentWidget();
    if (w) w.setAttribute('data-pa-active', active ? 'true' : 'false');
  }

  function syncOrderProtection(cart) {
    var cb = currentCheckbox();
    var label = document.querySelector('[data-pa-price-label]');
    if (!cb) return;

    var paLine = findPALine(cart);
    var subCents = subtotalExcludingPA(cart);
    var tier = tierForSubtotal(subCents);

    var newLabel = subCents > 0 ? formatMoney(priceForTier(tier)) : '';
    if (label && label.textContent !== newLabel) label.textContent = newLabel;

    var inCart = !!paLine;
    cb.checked = inCart;
    setActiveVisual(inCart);

    // Only self-heal when idle, so we never fight an in-flight reconcile.
    if (!isBusy) {
      if (inCart && (paLine.id !== tier.id || paLine.quantity !== 1) && subCents > 0) {
        reconcile(true);
      }
      if (inCart && subCents <= 0) {
        reconcile(false);
      }
    }
  }

  function onToggle(e) {
    setActiveVisual(e.target.checked);
    reconcile(e.target.checked);
  }

  function bindOrderProtection() {
    var cb = currentCheckbox();
    if (!cb) return;
    if (cb.dataset.paBound === '1') return;
    cb.dataset.paBound = '1';
    cb.addEventListener('change', onToggle);
  }

  // ========================================================================
  // FEATURE 2: FREE SHIPPING BAR
  // ========================================================================

  function syncShippingBar(cart) {
    var bar = document.querySelector('[data-fs-bar]');
    if (!bar) return;

    var threshold = parseFloat(bar.getAttribute('data-fs-threshold')) || 0;
    if (threshold <= 0) { bar.hidden = true; return; }

    var subCents = subtotalExcludingPA(cart);
    var subDollars = subCents / 100;

    // Hide entirely on empty cart.
    if (subCents <= 0) { bar.hidden = true; return; }
    bar.hidden = false;

    var msg = bar.querySelector('[data-fs-message]');
    var fill = bar.querySelector('[data-fs-fill]');
    var track = bar.querySelector('.fs-bar__track');

    var pct = Math.min(100, (subDollars / threshold) * 100);
    var pctStr = pct.toFixed(1);
    // Only touch the DOM when values actually change — identical writes still
    // fire MutationObserver records and would feed a sync loop.
    if (fill && fill.dataset.fsPct !== pctStr) {
      fill.style.setProperty('width', pct + '%', 'important');
      fill.dataset.fsPct = pctStr;
    }
    if (track) {
      var pctRound = String(Math.round(pct));
      if (track.getAttribute('aria-valuenow') !== pctRound) {
        track.setAttribute('aria-valuenow', pctRound);
      }
    }

    var stateKey, html;
    if (subDollars >= threshold) {
      stateKey = 'unlocked';
      html = '<span class="fs-amount">You\'ve unlocked FREE SHIPPING!</span>';
    } else {
      var remaining = threshold - subDollars;
      stateKey = 'away:' + formatMoney(remaining);
      html = "You're <span class=\"fs-amount\">" + formatMoney(remaining) +
             "</span> away from free shipping";
    }
    if (msg && msg.dataset.fsState !== stateKey) {
      msg.innerHTML = html;
      msg.dataset.fsState = stateKey;
    }
  }

  // ========================================================================
  // SHARED SYNC + WATCHERS
  // ========================================================================

  // Single cart fetch, then update BOTH features.
  function syncAll() {
    return getCart().then(function (cart) {
      bindOrderProtection();
      bindInfoTrigger();
      syncOrderProtection(cart);
      syncShippingBar(cart);
      // patchVisibleTotals covers subtotal + checkout button + count bubble,
      // so third-party/Dawn cart writes keep the footer accurate too (the
      // drawer section swap no-ops in this theme). All writes value-guarded.
      patchVisibleTotals(cart);
      // Keep row line indices honest after ANY cart change (see remapLineIndices).
      remapLineIndices(cart);
      // Show/hide the empty state from the garments-only count (see syncEmptyState).
      syncEmptyState(cart);
      // Keep Dawn's cached item count honest after any cart change.
      syncDawnItemCount(cart);
    }).catch(function () {});
  }

  // ========================================================================
  // iOS GHOST-OVERLAY FIX
  // ========================================================================
  // On iOS Safari, after the drawer's DOM has been swapped (add-to-cart
  // re-render) the close transition can leave a GHOSTED compositing layer:
  // computed styles say hidden, but the dim stays painted (no hit-target).
  // CSS cannot clear a ghost — the layer must be destroyed and rebuilt.
  // When the drawer finishes closing, briefly display:none the container,
  // force a reflow, and restore. Invisible to the user; kills the ghost.
  // Uses its own attribute-only observer (class changes don't fire the main
  // childList observer, and the style toggle here doesn't either — no loops).
  function watchDrawerGhost() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__paGhostWatched) return;
    drawer.__paGhostWatched = true;
    new MutationObserver(function () {
      if (drawer.classList.contains('active')) return;
      // Wait out Dawn's close transition, then nuke the painted layer.
      // IMPORTANT: in stock Dawn the grey dim is the <cart-drawer> element's
      // OWN background (rgba shadow), not a child overlay — so the layer we
      // destroy must be the drawer element itself. Toggling a child does not
      // rebuild the parent's paint.
      setTimeout(function () {
        if (drawer.classList.contains('active')) return; // reopened meanwhile
        var prev = drawer.style.display;
        drawer.style.display = 'none';
        void drawer.offsetHeight; // force reflow — layer destroyed here
        drawer.style.display = prev || '';
      }, 450);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  var observer = null;
  function watchCartMutations() {
    if (observer) observer.disconnect();
    observer = new MutationObserver(debounce(function () { syncAll(); }, 250));
    var targets = [
      document.querySelector('cart-drawer'),
      document.querySelector('#CartDrawer'),
      document.querySelector('#main-cart-items'),
      document.querySelector('cart-items'),
      document.querySelector('#cart')
    ].filter(Boolean);
    targets.forEach(function (t) { observer.observe(t, { childList: true, subtree: true }); });
  }

  function patchFetch() {
    if (window.__ccFetchPatched) return;
    window.__ccFetchPatched = true;
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = (typeof url === 'string') ? url : (url && url.url) || '';
      var isCartWrite = /\/cart\/(change|add|update|clear)(\.js)?/.test(urlStr);
      var promise = origFetch.apply(this, arguments);
      if (isCartWrite && !isBusy) {
        promise.then(function () {
          setTimeout(function () {
            syncAll();
            var cb = currentCheckbox();
            if (cb && cb.checked) reconcile(true);
          }, 300);
        }).catch(function () {});
      }
      return promise;
    };
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // ========================================================================
  // INFO MODAL — "what's included" popup
  // ========================================================================
  // The (i) button opens a centered modal. We move the modal (from the
  // <template>) to <body> so cart-drawer re-renders can't destroy it while open.

  var paModalEl = null; // the live modal element in <body>, when open

  function openModal() {
    if (paModalEl) return; // already open
    var tpl = document.querySelector('[data-pa-modal-template]');
    if (!tpl || !tpl.content) return;
    var node = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(node);
    paModalEl = node;
    document.body.style.overflow = 'hidden'; // lock scroll behind modal

    // Close handlers: clicking the X, the backdrop, or anywhere on the overlay
    // outside the dialog closes the modal. Clicks inside the dialog do not.
    node.querySelectorAll('[data-pa-modal-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });
    // Whole overlay closes on click...
    node.addEventListener('click', closeModal);
    // ...but clicks inside the dialog are stopped before they reach the overlay.
    var dlg = node.querySelector('.pa-modal__dialog');
    if (dlg) {
      dlg.addEventListener('click', function (e) { e.stopPropagation(); });
    }
    document.addEventListener('keydown', onModalKey);

    // Move focus to the dialog for accessibility
    var dialog = node.querySelector('.pa-modal__dialog');
    if (dialog) { dialog.setAttribute('tabindex', '-1'); dialog.focus(); }
  }

  function closeModal() {
    if (!paModalEl) return;
    document.removeEventListener('keydown', onModalKey);
    paModalEl.remove();
    paModalEl = null;
    document.body.style.overflow = ''; // restore scroll
  }

  function onModalKey(e) {
    if (e.key === 'Escape') closeModal();
  }

  // Bind the (i) trigger. Safe to call repeatedly (guarded).
  function bindInfoTrigger() {
    var btn = document.querySelector('[data-pa-info]');
    if (!btn) return;
    if (btn.dataset.paInfoBound === '1') return;
    btn.dataset.paInfoBound = '1';
    btn.addEventListener('click', function (e) {
      // Prevent the click from toggling the checkbox (the (i) sits inside the label)
      e.preventDefault();
      e.stopPropagation();
      openModal();
    });
  }

  function init() {
    // Load live prices first so the displayed price matches Shopify, then sync.
    loadLivePrices().then(syncAll);
    bindInfoTrigger();
    watchCartMutations();
    watchDrawerGhost();
    patchFetch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

})();
